<script setup lang="ts">
// Atlas mobile bottom tab bar — Home · Live · Events · Guide · More.
// Replaces the legacy hamburger drawer; visible only below --bp-lg (the
// desktop top NavBar covers larger viewports). Events is a primary tab (the
// product spine, spec §4); Library (VOD) + the secondary + admin-gated
// destinations live under "More". Touch targets are ≥44px (spec §2.4).

import { computed, ref } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useInstallPrompt } from '@/composables/useInstallPrompt';
import Modal from '@/components/primitives/Modal.vue';

const route = useRoute();
const auth = useAuthStore();
const installPrompt = useInstallPrompt();

const moreOpen = ref(false);

const homeActive = computed(() => route.path === '/home' || route.path === '/');
const liveActive = computed(() => route.path === '/live');
const eventsActive = computed(() => route.path === '/events');
const guideActive = computed(() => route.path === '/guide');

function closeMore() {
    moreOpen.value = false;
}

async function logout() {
    moreOpen.value = false;
    await auth.logout().catch(() => {});
    location.href = '/login';
}

async function install() {
    await installPrompt.installApp();
    moreOpen.value = false;
}
</script>

<template>
    <nav class="bottom-tab-bar" aria-label="Primary">
        <RouterLink to="/home" class="tab" :class="{ active: homeActive }" :aria-current="homeActive ? 'page' : undefined">
            <svg class="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M3 11l9-8 9 8M5 10v10h14V10" />
            </svg>
            <span>Home</span>
        </RouterLink>
        <RouterLink to="/live" class="tab" :class="{ active: liveActive }" :aria-current="liveActive ? 'page' : undefined">
            <svg class="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <rect x="2" y="6" width="20" height="13" rx="2" />
                <path d="M8 3l4 3 4-3" />
            </svg>
            <span>Live</span>
        </RouterLink>
        <RouterLink to="/events" class="tab" :class="{ active: eventsActive }" :aria-current="eventsActive ? 'page' : undefined">
            <svg class="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M13 2L4 14h6l-1 8 9-12h-6z" />
            </svg>
            <span>Events</span>
        </RouterLink>
        <RouterLink to="/guide" class="tab" :class="{ active: guideActive }" :aria-current="guideActive ? 'page' : undefined">
            <svg class="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <path d="M3 9h18M8 2v4M16 2v4" />
            </svg>
            <span>Guide</span>
        </RouterLink>
        <button type="button" class="tab tab-more" :class="{ active: moreOpen }" :aria-expanded="moreOpen" @click="moreOpen = true">
            <svg class="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M3 6h18M3 12h18M3 18h18" />
            </svg>
            <span>More</span>
        </button>
    </nav>

    <Modal v-model:open="moreOpen" title="More" sheet @close="closeMore">
        <nav class="more-sheet" aria-label="More navigation">
            <!-- U7 (ux-remediation X5) — identity line, same copy as AppRail's
                 Account popover. -->
            <p v-if="auth.user" class="more-identity" data-testid="more-identity">
                Signed in as {{ auth.user.username }} · {{ auth.user.role }}
            </p>
            <RouterLink to="/movies" class="more-link" @click="closeMore">Movies</RouterLink>
            <RouterLink to="/series" class="more-link" @click="closeMore">Series</RouterLink>
            <RouterLink to="/favourites" class="more-link" @click="closeMore">Favourites</RouterLink>
            <RouterLink to="/recordings" class="more-link" @click="closeMore">Recordings</RouterLink>
            <!-- U8 (ux-remediation X6) — the desktop rail shows Manage
                 un-gated (no admin check); mobile had no way to reach it at
                 all. Deliberately OUTSIDE the admin-only block below. -->
            <RouterLink to="/manage" class="more-link" @click="closeMore">Manage</RouterLink>
            <!-- D8 final (ux-remediation X5) — Settings reachable by every
                 role; Sources + Logs stay admin-only. -->
            <RouterLink to="/settings" class="more-link" @click="closeMore">Settings</RouterLink>
            <template v-if="auth.isAdmin">
                <RouterLink to="/sources" class="more-link" @click="closeMore">Sources</RouterLink>
                <RouterLink to="/dev/logs" class="more-link" @click="closeMore">Logs</RouterLink>
            </template>
            <hr class="more-sep" />
            <!-- Viewer-reachable by design — About carries the TMDB attribution. -->
            <RouterLink to="/about" class="more-link" @click="closeMore">About</RouterLink>
            <button v-if="installPrompt.canInstall.value" type="button" class="more-link" @click="install">Install app</button>
            <button v-if="auth.isAuthed" type="button" class="more-link more-signout" @click="logout">Sign out</button>
        </nav>
    </Modal>
</template>

<style scoped>
.bottom-tab-bar {
    display: none;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: var(--bottom-nav-height);
    padding-bottom: env(safe-area-inset-bottom, 0px);
    background: var(--color-bg-secondary);
    border-top: 1px solid var(--color-border);
    z-index: var(--z-bottom-nav);
}

.tab {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 3px;
    min-height: 44px; /* touch target — spec §2.4 */
    padding: 6px 0;
    color: var(--color-text-muted);
    text-decoration: none;
    font-size: 9.5px;
    font-weight: 600;
    transition: color var(--transition-fast);
}

.tab .ico {
    width: 20px;
    height: 20px;
}

.tab.active {
    color: var(--color-accent);
}

.tab:hover,
.tab:focus-visible {
    color: var(--color-text-primary);
}

.more-sheet {
    display: flex;
    flex-direction: column;
    gap: var(--space-xs);
}

.more-identity {
    margin: 0 0 var(--space-xs);
    padding: 0 var(--space-md);
    font-size: var(--fs-xs);
    color: var(--color-text-muted);
}

.more-link {
    display: block;
    width: 100%;
    text-align: left;
    color: var(--color-text-secondary);
    text-decoration: none;
    padding: var(--space-sm) var(--space-md);
    border-radius: var(--radius-sm);
    font-size: var(--fs-md);
    transition:
        color var(--transition-fast),
        background var(--transition-fast);
}

.more-link:hover,
.more-link:focus-visible {
    color: var(--color-text-primary);
    background: var(--color-bg-hover);
}

.more-signout:hover,
.more-signout:focus-visible {
    color: var(--color-error);
}

.more-sep {
    border: 0;
    border-top: 1px solid var(--color-border);
    margin: var(--space-xs) 0;
}

/* The tab bar is the PHONE tier's navigation only — from 640 up the AppRail
   owns it, and the rail carries every destination the "More" sheet does
   (mobile-foldable-redesign §2.2). */
@media (max-width: 639px) { /* --bp-sm */
    .bottom-tab-bar {
        display: flex;
    }
}
</style>
